/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React from "react"
import { TouchableOpacity, View } from "react-native"

import AnalyzeHomeIcon from "@/assets/clipart/widgets/analyze-home-icon.svg"
import MyLeadsHomeIcon from "@/assets/clipart/widgets/myleads-home-icon.svg"
import MyLeadsHomeInversedIcon from "@/assets/clipart/widgets/myleads-home-icon-inversed.svg"
import LoansHomeIcon from "@/assets/clipart/widgets/loans-home-icon.svg"
import RewardsHomeIcon from "@/assets/clipart/widgets/rewards-home-icon.svg"
import RewardsHomeIconInversed from "@/assets/clipart/widgets/rewards-home-icon-inversed.svg"
import WidgetNavIcon from "@/assets/clipart/widgets/widget-nav-icon.svg"
import PreApprovalLetterIcon from "@/assets/clipart/widgets/preapproval-letter-graphic.svg"
import Spacer from "@/components/util/Spacer"
import { cn } from "@/components/util/cn"
import BFText, { H6 } from "@/components/standard/BFText"

type WidgetCategory =
  | "analyze"
  | "properties"
  | "loans"
  | "rewards"
  | "events"
  | "preapproval"
  | "marketing"
type WidgetOrientation = "vertical" | "horizontal"
type WidgetVariant = "highlighted" | "new" | "default"

interface WidgetType {
  title: string
  category: WidgetCategory
  orientation: WidgetOrientation
  onPress?: () => void
  variant?: WidgetVariant
  children: React.ReactNode
}

interface WidgetTitleRowProps {
  title: string
  ctaText?: string
  category: WidgetCategory
  IconComponentOverride?: React.ElementType
  variant?: WidgetVariant
}

export const WidgetTitleRow = ({
  title,
  ctaText,
  category,
  IconComponentOverride,
  variant = "default",
}: WidgetTitleRowProps) => {
  const commonIconProps = {
    width: 15,
    height: 15,
  }
  const getIconComponent = (category: WidgetCategory) => {
    switch (category) {
      case "analyze":
        return <AnalyzeHomeIcon {...commonIconProps} />
      case "properties":
        return variant === "highlighted" ? (
          <MyLeadsHomeInversedIcon {...commonIconProps} />
        ) : (
          <MyLeadsHomeIcon {...commonIconProps} />
        )
      case "loans":
        return <LoansHomeIcon {...commonIconProps} />
      case "rewards":
        return variant === "new" ? (
          <RewardsHomeIconInversed {...commonIconProps} />
        ) : (
          <RewardsHomeIcon {...commonIconProps} />
        )
      case "preapproval":
        return <PreApprovalLetterIcon {...commonIconProps} />
      default:
        return null
    }
  }

  const IconComponent = IconComponentOverride
    ? React.createElement(IconComponentOverride)
    : getIconComponent(category)

  return (
    <View className="flex flex-row items-center justify-start w-full p-3">
      {IconComponent}
      <BFText
        className={cn(
          "ml-2 font-normal tracking-[0.12px] font-spacegrotesk text-[14px]",
          variant !== "default" ? "text-midnight-blue" : "text-white"
        )}
      >
        {title.toUpperCase()}
      </BFText>
      <Spacer />
      <View className="flex-row">
        {ctaText && <H6 className="text-white uppercase pr-2">{ctaText}</H6>}
        <WidgetNavIcon width={15} height={15} />
      </View>
    </View>
  )
}

export const WidgetWrapper: React.FC<WidgetType> = ({
  title,
  category,
  orientation,
  onPress,
  variant = "default",
  children,
}) => {
  return (
    <TouchableOpacity
      testID={`widget-${category}`}
      className={cn(
        variant === "highlighted"
          ? "bg-beige"
          : variant === "new"
            ? "bg-white"
            : "bg-calm-blue/50",
        "rounded-md my-[6px]",
        orientation === "vertical"
          ? "w-[160px] h-[184px] mx-[6px] justify-start items-start"
          : "w-[332px]"
      )}
      onPress={onPress}
    >
      {orientation === "vertical" ? (
        <>
          <WidgetTitleRow title={title} category={category} variant={variant} />
          <View className="flex-1 justify-start items-start mx-[10px] self-stretch">
            {children}
          </View>
        </>
      ) : (
        <View className="grow justify-center items-center">{children}</View>
      )}
    </TouchableOpacity>
  )
}
